import React, {useEffect, useState} from 'react';
import {getD365Payments, uploadD365PaymentsFile} from '../../api/d365Api';
import {PaymentsTable} from "./PaymentsTable";
import {Button, Checkbox, Col, Input, notification, PageHeader, Row, Select, Upload} from "antd";
import {ReloadOutlined, SearchOutlined, UploadOutlined} from "@ant-design/icons";
import _ from "lodash";
import {PREFERRED_DATE_FORMAT} from "../../util/time";
import moment from "moment";
import {useUserPermissions} from "../../auth/userHooks";
import {handleResponse} from "./utils";

const Payments = ({upload}) => {
  const [records, setRecords] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [hideColumns, setHideColumns] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const permissions = useUserPermissions();
  const statuses = ['IGNORED','OPEN','PAID','NOT FOUND'];
  const [statusesSelected,setStatusesSelected] = useState(['OPEN','NOT FOUND']);
  const [fileList, setFileList] = useState([]);

  const load = () => {
    try {
      setLoading(true);
      getD365Payments().then(payments => {
        setRecords(payments);
        filter(payments, searchText, statusesSelected)
      }).finally(
        () => setLoading(false)
      );
    } catch (e) {
      console.error(`Could not get Payments : ${e}`);
    }
  };

  const filter = (records, search, selected) => {
    var lcSearch = _.toLower(search);
    setFiltered(
      _.chain(records)
        .filter(
          (i) =>
            (search === "" ||
              _.includes(_.toLower(i.pendingInvoiceNumber), lcSearch) ||
              _.includes(_.toLower(i.invoiceNumber), lcSearch) ||
              _.includes(_.toLower(i.voucher), lcSearch) ||
              _.includes(_.toLower(i.projectorInvoice), lcSearch) ||
              _.includes(_.toLower(i.projectorName), lcSearch) ||
              _.includes(_.toLower(i.status), lcSearch) ||
              _.includes(moment(i.date).format(PREFERRED_DATE_FORMAT), lcSearch) ||
              _.includes(moment(i.projectorDate).format(PREFERRED_DATE_FORMAT), lcSearch)) &&
            (selected && selected.length && selected.indexOf(i.status) > -1)
        ).value()
    );
  };

  useEffect(() => {
    // this will make the search wait for a short delay after the user stops typing, so the search field will not appear to lag
    if (records.length > 0) {
      const timeOutId = setTimeout(() => filter(records, searchText, statusesSelected), 500);
      return () => clearTimeout(timeOutId);
    }
    // eslint-disable-next-line
  }, [searchText])


  const handleHideAuditing = (t) => {
    setHideColumns(!t.target.checked);
  };

  const statusSelectChange = (value) => {
    setStatusesSelected(value);
    filter(records,searchText, value);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const uploadPaymentsFile = file => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result) {
        setLoading(true);
        const data = _.replace(reader.result, 'data:text/csv;base64,', '');
        uploadD365PaymentsFile(data).then((resp) => {
          handleResponse(resp, 'Payments', 'upload')
        }).finally(() => {
          setLoading(false);
        })
      }
    };
  };

  const uploadProps = {
    beforeUpload: file => {
      setFileList([]);

      if (file && file.type === 'text/csv') {
        uploadPaymentsFile(file);
      } else {
        notification.error({
          message: 'Error',
          description: `Payments file must be in CSV format`,
          placement: 'bottomRight',
        });
      }

      return false;
    },
    fileList,
    maxCount: 1
  };

  return (
    <>
      <Row style={{marginBottom: 10}}>
        {!upload && (
          <Col span={24}>
            <PageHeader
              title="D365 Payments"
              subTitle={`(${filtered.length} shown of ${records.length} total)`}
              style={{padding: 0}}
            />
          </Col>
        )}
        <Col span={6}>
          <Input
            id="payments-search"
            style={{maxWidth: 400, marginRight: 10}}
            placeholder="Search"
            prefix={<SearchOutlined/>}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
          {upload &&
            <span className="ant-page-header-heading-sub-title">
            ({filtered.length} shown)
          </span>
          }
        </Col>
        <Col span={10}>
          <span>Show Statuses</span>
          <Select
            loading={loading}
            showArrow
            onChange={statusSelectChange}
            defaultValue={statusesSelected}
            mode="multiple"
            placeholder="Select Statuses"
            clearIcon
            style={{width: 400, marginLeft: 10}}
          >
            {statuses.map((status) => (
              <Select.Option key={status} value={status}>
                {status}
              </Select.Option>
            ))}
          </Select>

        </Col>
        <Col flex={"auto"}>
          <Button
            style={{float: 'right', alignItems: 'center'}}
            onClick={() => load()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
          {
            permissions && permissions.canManageD365 && (
              <div style={{float: "right", marginRight: 10}}>
              <Upload {...uploadProps}>
                <Button loading={loading} icon={<UploadOutlined/>}>Upload Payments CSV</Button>
              </Upload>
              </div>
            )
          }
          <Checkbox
            id="payments-hide-auditing"
            onChange={handleHideAuditing}
            disabled={loading}
            style={{float: 'right', marginRight: 10}}
          >
            Show Auditing
          </Checkbox>
        </Col>

      </Row>
      <PaymentsTable
        records={filtered}
        load={load}
        loading={loading}
        setLoading={setLoading}
        permissions={permissions}
        hideColumns={hideColumns}
      />
    </>
  );
};

export {Payments};